'use client';

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  AuditOutlined,
  DollarOutlined,
  HomeOutlined,
  LogoutOutlined,
  ProfileOutlined,
  RiseOutlined,
  SettingOutlined,
  SlidersFilled,
  UsergroupAddOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { faBarChart, faNewspaper } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Avatar,
  Button,
  Dropdown,
  Image,
  Menu,
  MenuProps,
  message,
} from 'antd';
import { Outlet, useNavigate, useParams } from 'react-router-dom';
import { useCookies } from 'react-cookie';
import { useConnection, useUser } from '@sezenta/connection';
import { MasterDataType, Permission } from '../../dto';
import { title } from 'radash';
import {
  getRefreshTokenCookie,
  isSuperAdmin,
  setLastContractId,
  useContracts,
} from '../../context/ContractHelpers';
import { usePermissions } from '../../auth/usePermissions';
import { SessionFreshnessWatcher } from '../../auth/SessionFreshnessWatcher';

type MenuItem = Required<MenuProps>['items'][number];

// CR004: shared/cross-contract master data — managed only in the super-admin
// portal, hidden from the per-contract Master Data menu.
const CROSS_CONTRACT_MD = ['State', 'Council', 'Suburb', 'Area'];

// UI display overrides for master-data types whose enum value reads poorly to
// users. The backend value / URL param are unchanged — label only.
const MD_MENU_LABEL: Record<string, string> = {
  SystemAdmin: 'Notification Recipients',
};

const NewMainLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [openKey, setOpenKey] = useState<string[]>();

  const navigate = useNavigate();
  const user = useUser();
  const connection = useConnection();
  // CR004: contract is in the path; build portal links against it.
  const { contractId } = useParams<{ contractId: string }>();
  const cid = contractId ?? '1';
  const [cookies] = useCookies(['refreshToken']);
  const { contracts } = useContracts();
  const { hasAny } = usePermissions();

  useEffect(() => {
    if (user === null) {
      navigate('/login');
    }
  }, [navigate, user]);

  // CR004: switch the active contract — re-issue a scoped token (refresh stays),
  // persist the choice, and navigate so the URL's :contractId matches the token.
  const switchContract = useCallback(
    async (targetId: string) => {
      if (targetId === cid) return;
      try {
        const sw = await connection.post(`auth/switch-contract/${targetId}`);
        // useUser() (and thus the visible permissions/menu) is derived from the
        // REFRESH token, so we MUST adopt the new contract-scoped refresh token
        // returned by switch-contract — otherwise the UI keeps the login
        // project's permissions. Fall back to the old cookie only if the backend
        // predates dual-token switch.
        connection.login(
          sw.data.accessToken,
          sw.data.refreshToken ??
            getRefreshTokenCookie() ??
            cookies.refreshToken,
        );
        setLastContractId(targetId);
        navigate(`/portal/${targetId}/dashboard`);
      } catch (e) {
        message.error('Could not switch project');
      }
    },
    [cid, connection, cookies.refreshToken, navigate],
  );

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const items: MenuItem[] = useMemo(() => {
    const dataChildren: MenuItem[] = [];

    if (
      hasAny([
        Permission.VIEW_USERS,
        Permission.CREATE_USER,
        Permission.EDIT_USER,
        Permission.DELETE_USER,
      ])
    ) {
      dataChildren.push({
        key: '1',
        label: 'Users',
        icon: null,
        onClick: () => {
          navigate(`/portal/${cid}/users`);
        },
      });
    }
    if (
      hasAny([
        Permission.VIEW_SITES,
        Permission.CREATE_SITE,
        Permission.EDIT_SITE,
        Permission.DELETE_SITE,
      ])
    ) {
      dataChildren.push({
        key: '2',
        label: 'Stores',
        icon: null,
        onClick: () => {
          navigate(`/portal/${cid}/stores`);
        },
      });
    }
    if (
      hasAny([
        Permission.VIEW_MASTER_DATA,
        Permission.CREATE_MASTER_DATA,
        Permission.EDIT_MASTER_DATA,
        Permission.DELETE_MASTER_DATA,
      ])
    ) {
      dataChildren.push({
        key: '3',
        label: 'Master Data',
        icon: null,
        children: Object.entries(MasterDataType)
          // CR004: cross-contract (shared) lists are managed only in the
          // super-admin portal. The contract keeps the per-contract types
          // (Zone, System Admin, Collection Type).
          .filter(([, value]) => !CROSS_CONTRACT_MD.includes(value))
          .map(([key, value]) => {
            return {
              key: value,
              label: MD_MENU_LABEL[value] ?? title(value),
              icon: null,
              onClick: () => {
                navigate(`/portal/${cid}/master-data/${value}`);
              },
            };
          }),
      });
    }

    const mainItems: any = [
      {
        key: 'dashboard',
        label: 'Dashboard',
        icon: <FontAwesomeIcon icon={faNewspaper} />,
        onClick: () => {
          navigate(`/portal/${cid}/dashboard`);
        },
      },
    ];
    if (hasAny([Permission.VIEW_REPORTS, Permission.EXPORT_COLLECTION])) {
      mainItems.push({
        key: 'Collection Report',
        label: 'Collection Report',
        icon: <FontAwesomeIcon icon={faBarChart} />,
        onClick: () => {
          navigate(`/portal/${cid}/collection-report`);
        },
      });
    }
    // The "Admin" group only appears when it has at least one child the user can
    // reach (Users / Stores / Master Data).
    if (dataChildren.length > 0) {
      mainItems.push({
        key: 'admin',
        label: 'Admin',
        icon: <RiseOutlined />,
        children: dataChildren,
      });
    }
    return mainItems;
  }, [navigate, hasAny, cid]);

  const onClick: MenuProps['onClick'] = (e) => {
    console.log('click ', e);
  };

  const onLogout = () => {
    connection.logout();
    navigate('/login');
  };

  // const onChangeOpen = useCallback((key: string[]) => {
  //   console.log('keyyyyy', key);
  //   if (key.length === 0) {
  //     setOpenKey(undefined);
  //   } else {
  //     setOpenKey(key);
  //   }
  // }, []);

  const onChangeOpen = (key: string[]) => {
    console.log('keyyyyy', key);
    if (key.length === 0) {
      setOpenKey(undefined);
    } else {
      setOpenKey(key);
    }
  };

  const menu = (
    <Menu>
      <Menu.Item key="logout" onClick={onLogout} icon={<LogoutOutlined />}>
        Logout
      </Menu.Item>
    </Menu>
  );

  return (
    <div className="w-full h-screen flex main-layout">
      <SessionFreshnessWatcher />
      <div className="w-full h-full flex">
        <div className="h-full w-fit bg-white side-nav flex flex-col">
          <div
            className="flex items-center px-4 h-[60px] min-h-[60px] max-h-[60px] w-full bg-white gap-2 mb-2 cursor-pointer"
            style={{
              boxShadow: '0px 1px 0px rgba(18, 32, 59, 0.09)',
            }}
            tabIndex={0}
            onClick={toggleCollapsed}
          >
            {!collapsed && (
              <img
                src="/img/logo.png"
                alt="logo"
                className="w-[88px] h-[38px]"
              />
            )}
            {collapsed && (
              <img
                src="/img/logo2.png"
                alt="logo"
                className="w-[38px] h-[38px]"
              />
            )}
          </div>

          {!collapsed && (
            <div className="w-full flex justify-start items-center py-2 gap-2 px-4">
              <Image
                src={'/img/logo2.png'}
                alt={'logo'}
                preview={false}
                width={40}
                height={30}
              />
              <div className="flex flex-col gap-2 w-full">
                <div className="text-[#252C32] text-[14px] font-semibold">
                  Collection
                </div>
                <div className="text-[#84919A] text-[12px] font-normal">
                  Collection Management
                </div>
              </div>
            </div>
          )}
          <Menu
            className="h-full w-full bg-white"
            onClick={onClick}
            defaultSelectedKeys={['dashboard']}
            mode="inline"
            items={items}
            // defaultOpenKeys={openKey}
            openKeys={openKey}
            onOpenChange={onChangeOpen}
            inlineCollapsed={collapsed}
          />
        </div>

        <div className="h-full w-screen flex flex-col overflow-y-scroll">
          <div
            className="w-full h-[60px] min-h-[60px] max-h-[60px] bg-white items-center flex px-4 overflow-y-scroll"
            style={{
              boxShadow: '0px 1px 0px rgba(18, 32, 59, 0.09)',
            }}
          >
            <div className="flex items-center ml-auto pr-2 gap-5">
              {/* CR004: contract switcher when the user has >1 contract; a single
                  project just shows its name (no switcher needed). */}
              {contracts.length > 1 ? (
                <Dropdown
                  trigger={['click']}
                  menu={{
                    items: contracts.map((c) => {
                      // Projects where the user only has field (mobile) access are
                      // shown but disabled — they can't be opened on web.
                      const noWebAccess = c.webAccess === false;
                      return {
                        key: c.businessId,
                        label: noWebAccess
                          ? `${c.name ?? c.businessId} — mobile only`
                          : (c.name ?? c.businessId),
                        disabled: noWebAccess,
                        onClick: noWebAccess
                          ? undefined
                          : () => switchContract(c.businessId),
                      };
                    }),
                  }}
                >
                  <Button type="default" className="flex items-center">
                    {contracts.find((c) => c.businessId === cid)?.name ??
                      'Switch Project'}
                  </Button>
                </Dropdown>
              ) : contracts.length === 1 ? (
                <span
                  className="flex items-center gap-2 font-medium text-[#18191C]"
                  title="Your project"
                >
                  <span className="w-2 h-2 rounded-full bg-[#26A3A7]" />
                  {contracts.find((c) => c.businessId === cid)?.name ??
                    contracts[0]?.name ??
                    contracts[0]?.businessId}
                </span>
              ) : null}
              {/* CR004: super-admins can jump to the platform admin shell. */}
              {isSuperAdmin(user) && (
                <Button type="link" onClick={() => navigate('/admin/projects')}>
                  Admin
                </Button>
              )}
              <Button
                icon={<SettingOutlined />}
                type="text"
                className="text-[20px] text-gray-500"
                onClick={() => navigate(`/portal/${cid}/settings`)}
              ></Button>
              <Dropdown overlay={menu} trigger={['click']}>
                <div className="gap-2 flex items-center cursor-pointer">
                  <Avatar
                    icon={<UserOutlined />}
                    src={user?.picture}
                    className="cursor-pointer"
                  >
                    U
                  </Avatar>
                  <p>
                    {user?.firstName} {user?.lastName}
                  </p>
                </div>
              </Dropdown>
            </div>
          </div>
          <div className="w-full h-[calc(100%-60px)] px-4 mb-4">
            <div
              className="bg-white w-full h-full flex"
              style={{
                border: '1px solid #EAECF0',
                boxShadow: '0px 4px 8px -2px rgba(16, 24, 40, 0.1)',
                borderRadius: '8px',
              }}
            >
              <Outlet />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewMainLayout;
